import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles, Sun, Sunset, Moon, Printer, Plus, RefreshCw, PenLine, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useOrariTurni, orarioLabel } from "../lib/orari";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { ScriviConsegnaNew } from "./ScriviConsegnaNew";
import { NomiBlu, turnoAdesso, ymd } from "./ConsegneVistaNew";
import { useFirma } from "../context/FirmaContext";

const TURNI = [["mattina", "Mattina", Sun], ["pomeriggio", "Pomeriggio", Sunset], ["notte", "Notte", Moon]];
const TL = { mattina: "Mattina", pomeriggio: "Pomeriggio", notte: "Notte" };
const ADMIN = ["super_admin", "admin", "responsabile", "coordinatore"];
const oraDi = (iso) => (iso ? new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" }) : "");

function TestoAi({ testo, nomi }) {
  return testo.split("\n").map((l) => l.trim()).filter(Boolean).map((l, i) => (
    l.startsWith("#") ? <h3 key={i} className="font-bold text-[17px] mt-5 mb-1"><NomiBlu testo={l.replace(/^#+\s*/, "").replace(/\*\*/g, "")} nomi={nomi} /></h3>
      : <p key={i} className="mb-2"><NomiBlu testo={l.replace(/\*\*/g, "").replace(/^[-•]\s*/, "")} nomi={nomi} /></p>
  ));
}

function Nota({ n, nomi }) {
  const [orig, setOrig] = useState(false);
  return (
    <div className="py-2 border-b last:border-0" data-testid={`va-nota-${n.id}`}>
      <p>{n.imp && <b className="mr-1">★</b>}{n.ospite && <><span className="font-semibold text-blue-700">{n.ospite}</span> — </>}<NomiBlu testo={n.riscritta || n.originale} nomi={nomi} /></p>
      <div className="text-xs text-muted-foreground mt-0.5">Scritta da {n.operatore || "—"}{n.ora ? ` alle ${n.ora}` : ""}{n.riscritta ? " · resa più chiara dall'AI · " : ""}
        {n.riscritta && <button className="underline" onClick={() => setOrig(!orig)} data-testid={`va-nota-orig-${n.id}`}>{orig ? "nascondi originale" : "vedi originale"}</button>}</div>
      {orig && <p className="text-sm text-slate-600 mt-1 italic">«{n.originale}»</p>}
    </div>
  );
}

function Firme({ v, sel, onDone }) {
  const { signerName } = useFirma();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const io = signerName || user?.name;
  const firmato = v.letture.some((l) => l.nome === io);
  const firma = async () => {
    setBusy(true);
    try { await api.post("/consegne/vista-ai/firma", { data: sel.data, turno: sel.turno, structure_id: v.structure_id, operatore_nome: signerName || undefined }); toast.success("Firmato: hai letto la consegna"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore nella firma"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-5 rounded-lg border bg-slate-50 p-4" data-testid={`va-firme-${v.structure_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <b className="text-[15px]">Presa visione</b>
        <span className="text-sm text-muted-foreground">{v.letture.length} {v.letture.length === 1 ? "ha letto" : "hanno letto"}{v.attesi.length ? ` · ${v.mancano.length} ${v.mancano.length === 1 ? "manca" : "mancano"} del turno di ${v.turno_dopo}` : ""}</span>
        <Button onClick={firma} disabled={busy || firmato} className="ml-auto font-semibold" data-testid={`va-firma-${v.structure_id}`}>
          <CheckCircle2 size={15} className="mr-1" />{firmato ? `Hai già firmato (${io})` : busy ? "Firmo…" : "Ho letto · firma"}</Button>
      </div>
      <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
        <div><div className="font-semibold mb-1">Hanno letto</div>{v.letture.length ? <div className="flex flex-wrap gap-1.5">{v.letture.map((l) => <span key={l.nome} className="rounded-full border bg-white px-2.5 py-0.5" data-testid="va-letto"><CheckCircle2 size={12} className="inline mr-1 -mt-0.5" />{l.nome} · {oraDi(l.at)}</span>)}</div> : <p className="text-muted-foreground">Ancora nessuno.</p>}</div>
        <div><div className="font-semibold mb-1">Mancano</div>{v.attesi.length ? (v.mancano.length ? <div className="flex flex-wrap gap-1.5">{v.mancano.map((n) => <span key={n} className="rounded-full border border-dashed bg-white px-2.5 py-0.5 text-slate-600" data-testid="va-manca">{n}</span>)}</div> : <p>Tutti quelli del turno di {v.turno_dopo} hanno letto.</p>) : <p className="text-muted-foreground">Turno di {v.turno_dopo} non pianificato nei Turni: non so chi deve ancora leggere.</p>}</div>
      </div>
    </div>
  );
}

function Scheda({ v, note, nomi, sel, admin, onGen, busy, onFirma }) {
  return (
    <div className="gr-card p-5 sm:p-6 max-w-4xl" data-testid={`va-scheda-${v.structure_id}`}>
      <div className="flex flex-wrap items-center gap-2 pb-3 border-b">
        <Sparkles size={18} className="text-slate-600" /><b className="text-lg">Consegna del turno di {TL[sel.turno]}{v.nome ? ` · ${v.nome}` : ""}</b>
        <span className="text-sm text-muted-foreground" data-testid={`va-stato-${v.structure_id}`}>{v.testo ? `Scritta dall'AI alle ${oraDi(v.generated_at)}${v.auto ? " · automatica a fine turno" : ""}` : v.finito ? "Non ancora scritta" : `L'AI la scriverà da sola a fine turno (ore ${v.fine_turno})`}</span>
        <span className="ml-auto flex gap-2">
          {admin && <Button variant="outline" size="sm" onClick={onGen} disabled={busy} data-testid={`va-genera-${v.structure_id}`}><RefreshCw size={14} className={`mr-1 ${busy ? "animate-spin" : ""}`} />{busy ? "Sto scrivendo…" : v.testo ? "Riscrivi" : v.finito ? "Scrivi ora" : "Anteprima ora"}</Button>}
          {v.testo && <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="va-stampa"><Printer size={14} className="mr-1" /> Stampa</Button>}
        </span>
      </div>
      <div className="text-[15px] sm:text-[16px] leading-7 text-slate-800" data-testid={`va-testo-${v.structure_id}`}>
        {v.testo ? <TestoAi testo={v.testo} nomi={nomi} /> : <p className="mt-4 text-muted-foreground">{busy ? "L'AI sta scrivendo la consegna…" : "Qui comparirà il discorso scritto dall'AI con tutto quello che è successo nel turno."}</p>}
      </div>
      {v.testo && <Firme v={v} sel={sel} onDone={onFirma} />}
      <div className="mt-6 pt-3 border-t">
        <h3 className="font-bold text-[17px] mb-1 flex items-center gap-2"><PenLine size={16} className="text-slate-500" /> Note degli operatori</h3>
        {note.length ? <div className="text-[15px] leading-7">{note.map((n) => <Nota key={n.id} n={n} nomi={nomi} />)}</div> : <p className="text-sm text-muted-foreground">Nessuna nota scritta in questo turno.</p>}
      </div>
    </div>
  );
}

export default function ConsegneVistaAi({ structureId }) {
  const orari = useOrariTurni(structureId);
  const { structures, user } = useAuth();
  const qc = useQueryClient();
  const [sel, setSel] = useState(() => turnoAdesso(orari));
  const [scrivi, setScrivi] = useState(false);
  const [busy, setBusy] = useState("");
  const key = ["consegne-vista-ai", sel.data, sel.turno, structureId];
  const { data: d, isLoading, error } = useQuery({ queryKey: key, queryFn: async () => (await api.get("/consegne/vista-ai", { params: { data: sel.data, turno: sel.turno, structure_id: structureId } })).data, refetchInterval: 60000 });
  const genera = async (sid) => {
    setBusy(sid);
    try { await api.post("/consegne/vista-ai/genera", { data: sel.data, turno: sel.turno, structure_id: sid }); qc.invalidateQueries({ queryKey: ["consegne-vista-ai"] }); }
    catch (e) { toast.error(e?.response?.data?.detail || "L'AI non ha risposto, riprova"); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-3" data-testid="consegne-vista-ai">
      <div className="grid grid-cols-3 sm:flex sm:flex-wrap items-center gap-2">
        {TURNI.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setSel((p) => ({ ...p, turno: k }))} data-testid={`va-turno-${k}`}
            className={`inline-flex flex-wrap justify-center items-center gap-x-1.5 rounded-lg border px-2 sm:px-3 py-1 min-h-9 text-sm font-semibold ${sel.turno === k ? "bg-primary text-white border-primary" : "bg-white hover:bg-secondary"}`}>
            <Icon size={14} /> {l} <span className="font-normal opacity-75 text-xs">{orarioLabel(orari[k])}</span>
          </button>
        ))}
        <Input type="date" value={sel.data} max={ymd(new Date())} onChange={(e) => e.target.value && setSel((p) => ({ ...p, data: e.target.value }))} className="col-span-3 sm:w-44 h-9" data-testid="va-data" />
        <Button onClick={() => setScrivi(true)} className="font-semibold col-span-3 sm:col-span-1 sm:ml-auto" data-testid="va-scrivi"><Plus size={16} className="mr-1" /> Scrivi consegna</Button>
      </div>
      {isLoading && <div className="gr-card p-6 text-sm text-muted-foreground">Caricamento… (l'AI sta sistemando le note nuove)</div>}
      {error && <div className="gr-card p-6 text-sm text-red-700" data-testid="va-errore">{error?.response?.data?.detail || "Errore nel caricamento"}</div>}
      {d?.voci.map((v) => <Scheda key={v.structure_id} v={v} note={d.note.filter((n) => n.structure_id === v.structure_id)} nomi={d.nomi} sel={sel}
        admin={ADMIN.includes(user?.role)} onGen={() => genera(v.structure_id)} busy={busy === v.structure_id} onFirma={() => qc.invalidateQueries({ queryKey: ["consegne-vista-ai"] })} />)}
      <ScriviConsegnaNew open={scrivi} onClose={() => setScrivi(false)} structureId={structureId} structures={structures} orari={orari}
        onSaved={() => qc.invalidateQueries({ queryKey: ["consegne-vista-ai"] })} />
    </div>
  );
}
